import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';
import { formatCurrency } from '@/utils/currency';

const MenuManagement = ({ storeId }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: 'PIZZA',
    description: '',
    image_url: '',
    active: true
  });

  useEffect(() => {
    loadMenu();
  }, [storeId]);

  const loadMenu = async () => {
    try {
      const response = await apiClient.get(`/menu?store_id=${storeId}&active_only=false`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Error loading menu:', error);
      toast.error('Failed to load menu');
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      price: item.price,
      category: item.category,
      description: item.description || '',
      image_url: item.images?.image || '',
      active: item.active !== false
    });
    setShowEditModal(true);
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      price: '',
      category: 'PIZZA',
      description: '',
      image_url: '',
      active: true
    });
    setShowAddModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await apiClient.put(`/menu/${selectedItem.id}`, {
        name: formData.name,
        price: parseFloat(formData.price),
        category: formData.category,
        description: formData.description,
        active: formData.active,
        images: formData.image_url ? {
          image: formData.image_url,
          fallback: formData.image_url,
          thumbnail: formData.image_url
        } : undefined
      });
      toast.success('Menu item updated successfully');
      setShowEditModal(false);
      loadMenu();
    } catch (error) {
      console.error('Error updating menu item:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update menu item';
      toast.error(errorMessage);
    }
  };

  const handleSaveAdd = async () => {
    try {
      const payload = {
        name: formData.name,
        sku: `SKU-${Date.now()}`,
        price: parseFloat(formData.price),
        category: formData.category,
        description: formData.description || ''
      };
      
      if (formData.image_url) {
        payload.images = {
          image: formData.image_url,
          fallback: formData.image_url,
          thumbnail: formData.image_url
        };
      }
      
      await apiClient.post(`/menu?store_id=${storeId}`, payload);
      toast.success('Menu item added successfully');
      setShowAddModal(false);
      loadMenu();
    } catch (error) {
      console.error('Error adding menu item:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add menu item';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (itemId, itemName) => {
    if (!window.confirm(`Are you sure you want to delete "${itemName}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/menu/${itemId}`);
      toast.success('Menu item deleted successfully');
      loadMenu();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete menu item';
      toast.error(errorMessage);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Menu Management</h2>
        <Button onClick={handleAdd} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {menuItems.map((item) => (
          <Card key={item.id} className={item.active === false ? 'opacity-60 border-2 border-red-300' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex justify-between items-center">
                <span>{item.name}</span>
                <div className="flex gap-2">
                  {item.active === false && (
                    <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-800 font-bold">
                      OFF MENU
                    </span>
                  )}
                  <span className={`text-sm px-2 py-1 rounded ${item.active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.active !== false ? 'Available' : 'Unavailable'}
                  </span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium">{item.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Price:</span>
                  <span className="font-bold text-green-600">{formatCurrency(item.price)}</span>
                </div>
                {item.description && (
                  <p className="text-sm text-gray-600 mt-2">{item.description}</p>
                )}
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(item)}
                    className="flex-1"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(item.id, item.name)}
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Menu Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Item Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter item name"
              />
            </div>
            <div>
              <Label>Price (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="Enter price"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIZZA">Pizza</SelectItem>
                  <SelectItem value="BURGER">Burger</SelectItem>
                  <SelectItem value="PASTA">Pasta</SelectItem>
                  <SelectItem value="SIDES">Sides</SelectItem>
                  <SelectItem value="BEVERAGE">Beverage</SelectItem>
                  <SelectItem value="DESSERT">Dessert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter description"
              />
            </div>
            <div>
              <Label>Image URL (Optional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {formData.image_url && (
                <div className="mt-2">
                  <img src={formData.image_url} alt="Preview" className="w-32 h-32 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label>Available for order</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Menu Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Item Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter item name"
              />
            </div>
            <div>
              <Label>Price (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="Enter price"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIZZA">Pizza</SelectItem>
                  <SelectItem value="BURGER">Burger</SelectItem>
                  <SelectItem value="PASTA">Pasta</SelectItem>
                  <SelectItem value="SIDES">Sides</SelectItem>
                  <SelectItem value="BEVERAGE">Beverage</SelectItem>
                  <SelectItem value="DESSERT">Dessert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter description"
              />
            </div>
            <div>
              <Label>Image URL (Optional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {formData.image_url && (
                <div className="mt-2">
                  <img src={formData.image_url} alt="Preview" className="w-32 h-32 object-cover rounded" onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label>Available for order</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAdd} className="bg-green-600 hover:bg-green-700">
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuManagement;
